import { useStyletron } from 'baseui';
import { Alert } from 'baseui/icon';
import { Input, InputProps } from 'baseui/input';
import { colors } from 'baseui/tokens';
import { themedStyled } from 'common/styles/styletron.theme';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import NumberFormat, { NumberFormatPropsBase } from 'react-number-format';
import { useDebouncedCallback } from 'use-debounce';

import CustomFormControl, {
  CustomFormControlProps,
} from '../custom-form-control';
import { BaseElementInputProps } from '../field-type';
import { FormContext } from '../form';
import { styles } from '../styles';
import Text from '../text';

export interface TextFieldNumberProps
  extends BaseElementInputProps,
    Omit<NumberFormatPropsBase, 'name' | 'type'>,
    Omit<
      InputProps,
      | 'name'
      | 'type'
      | 'step'
      | 'value'
      | 'max'
      | 'min'
      | 'size'
      | 'onChange'
      | 'inputMode'
      | 'aria-autocomplete'
      | 'aria-haspopup'
      | 'onBlur'
      | 'onFocus'
      | 'onKeyDown'
      | 'onKeyPress'
      | 'onKeyUp'
      | 'role'
    >,
    Omit<
      CustomFormControlProps,
      'name' | 'overrides' | 'positive' | 'error' | 'children'
    > {
  type: 'number';
  maxEnchancer?: string;
  hint?: string;
  onAfterChange?: (value: any) => void;
  fieldType?: 'control' | 'uncontrol' | any;
  textAlign?: 'left' | 'right';
  defaultValue?: number;
}

function Negative() {
  const [css, theme] = useStyletron();

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        paddingRight: theme.sizing.scale500,
        color: theme.colors.negative400,
      })}
    >
      <Alert size="18px" />
    </div>
  );
}

export default function TextFieldNumber(props: TextFieldNumberProps) {
  const { control } = useFormContext<any>();
  const [customError, setCustomError] = React.useState('');
  const watchValue = useWatch({ control, name: props.name });
  const { field, fieldState } = useController({ name: props.name });
  const context = React.useContext(FormContext);
  const {
    readOnly,
    hideLabel,
    type,
    placeholder,
    label = '',
    max,
    maxEnchancer,
    overrides,
    hint,
    onAfterChange,
    clearable = true,
    textAlign = 'right',
    fieldType = 'control',
    disabled: _disabled,
    defaultValue,
    ...rest
  } = props;
  const ref = React.useRef<any>();
  const disabled = !context.editable || _disabled;

  const { t } = useTranslation();
  const onQueryChange = useDebouncedCallback(
    (event) => {
      onAfterChange?.(event?.target?.value);
    },
    800,
    {
      trailing: true,
    },
  );

  React.useEffect(() => {
    if (max && watchValue && watchValue > max) {
      setCustomError(
        t('common:max_number_error', {
          number: max,
          endEnchancer: maxEnchancer || '',
        }),
      );
    }
  }, [max, maxEnchancer, props.name, t, watchValue]);

  if (readOnly) {
    return (
      <CustomFormControl {...rest} label={label} hideLabel={hideLabel}>
        <StyledReadOnlyNumberFormat
          {...rest}
          thousandSeparator
          onBlur={() => {}}
          value={watchValue}
          name={props.name}
          customInput={Input}
          overrides={{
            ...overrides,
            Input: {
              props: {
                readOnly: 'true',
              },
              // ...overrides?.Input,
              style: {
                paddingTop: '2px',
                paddingBottom: '2px',
              },
            },
            Root: {
              style: styles.reset,
              ...overrides?.Root,
            },
            StartEnhancer: {
              style: {
                backgroundColor: 'transparent',
              },
            },
            InputContainer: {
              style: styles.reset,
              ...overrides?.InputContainer,
            },
          }}
        />
      </CustomFormControl>
    );
  }
  const shouldShowError = !!(fieldState.error?.message || customError);

  return (
    <CustomFormControl
      {...rest}
      label={label}
      error={fieldState.error?.message || customError}
      hideLabel={hideLabel}
      disabled={disabled}
    >
      <StyledDiv>
        <StyledNumberFormat
          {...rest}
          placeholder={placeholder}
          thousandSeparator
          getInputRef={ref}
          onBlur={field.onBlur}
          value={
            fieldType === 'uncontrol'
              ? watchValue
              : field.value !== undefined &&
                field.value !== null &&
                !isNaN(field.value) &&
                field.value !== ''
              ? field.value
              : defaultValue || 0
          }
          disabled={disabled}
          error={shouldShowError}
          clearable={clearable}
          overrides={{
            ...(shouldShowError ? { After: Negative } : {}),
            Input: {
              // ...overrides?.Input,
              style: {
                paddingTop: '2px',
                paddingBottom: '2px',
                textAlign,
                ...(disabled ? styles.reset : styles.active),
              },
            },
            Root: {
              style: {
                paddingRight: '0px !important',
                ...(disabled ? styles.reset : styles.active),
              },
            },
            StartEnhancer: {
              style: {
                backgroundColor: 'transparent',
              },
            },
            InputContainer: {
              style: {
                ...(disabled ? styles.reset : styles.active),
              },
            },
            ...overrides,
          }}
          customInput={Input}
          name={props.name}
          onChange={(ev: any) => {
            const temp = ev.currentTarget.value.toString().replace(/,/g, '');
            field.onChange(
              temp !== undefined && temp !== null && !isNaN(temp) && temp !== ''
                ? parseFloat(temp)
                : defaultValue || 0,
            );
            onAfterChange && onQueryChange(ev);
          }}
          onKeyDown={(ev: any) => {
            const value = ev.currentTarget.value;

            if (
              (ev.key === 'Backspace' || ev.key === 'Delete') &&
              value.length === 1 &&
              value === '0'
            ) {
              ev.preventDefault();
            }
          }}
        />
        {!!hint && (
          <Text
            variant="body2Regular"
            $style={{ marginTop: '8px', color: colors.gray400 }}
          >
            {hint}
          </Text>
        )}
      </StyledDiv>
    </CustomFormControl>
  );
}

export const StyledNumberFormat = themedStyled(
  NumberFormat,
  ({ $theme }) => ({}),
);

export const StyledReadOnlyNumberFormat = themedStyled(
  NumberFormat,
  ({ $theme }) => {
    return {};
  },
);

export const StyledDiv = themedStyled('div', ({ $theme }) => {
  return {};
});
